import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  FileText,
  MessageCircle,
  CheckCircle,
  XCircle,
  RefreshCw,
  UserPlus,
  Clock,
} from "lucide-react";
import { formatDistanceToNow, isToday, isYesterday, format } from "date-fns";

interface ActivityFeedProps {
  dealId: string;
}

interface Activity {
  id: string;
  deal_id: string;
  user_id: string | null;
  activity_type: string;
  activity_data: any;
  description: string;
  created_at: string;
  profiles?: {
    name: string;
  };
}

const activityIcons = {
  status_changed: RefreshCw,
  document_uploaded: FileText,
  comment_added: MessageCircle,
  deal_approved: CheckCircle,
  deal_rejected: XCircle,
  user_assigned: UserPlus,
  renewal_initiated: Clock,
};

const getDateLabel = (date: Date) => {
  if (isToday(date)) return "Today";
  if (isYesterday(date)) return "Yesterday";
  return format(date, "MMMM d, yyyy");
};

const groupActivitiesByDate = (activities: Activity[]) => {
  const groups: Record<string, Activity[]> = {};

  activities.forEach((activity) => {
    const date = new Date(activity.created_at);
    const label = getDateLabel(date);
    if (!groups[label]) {
      groups[label] = [];
    }
    groups[label].push(activity);
  });

  return groups;
};

export const ActivityFeed = ({ dealId }: ActivityFeedProps) => {
  const queryClient = useQueryClient();

  const { data: activities, isLoading } = useQuery({
    queryKey: ["deal-activities", dealId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("deal_activities" as any)
        .select(`
          *,
          profiles:user_id (
            name
          )
        `)
        .eq("deal_id", dealId)
        .order("created_at", { ascending: false })
        .limit(100);

      if (error) throw error;
      return data as unknown as Activity[];
    },
  });

  // Subscribe to realtime updates
  useEffect(() => {
    const channel = supabase
      .channel(`deal-activities-${dealId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "deal_activities",
          filter: `deal_id=eq.${dealId}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ["deal-activities", dealId] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [dealId, queryClient]);

  if (isLoading) {
    return <div className="text-center py-8">Loading activity...</div>;
  }

  const groupedActivities = activities ? groupActivitiesByDate(activities) : {};

  return (
    <Card>
      <CardHeader>
        <CardTitle>Activity Timeline</CardTitle>
      </CardHeader>
      <CardContent>
        <ScrollArea className="h-[500px] pr-4">
          <div className="space-y-6">
            {Object.entries(groupedActivities).map(([dateLabel, dateActivities]) => (
              <div key={dateLabel} className="space-y-4">
                <div className="flex items-center gap-2">
                  <Badge variant="outline">{dateLabel}</Badge>
                  <div className="flex-1 h-px bg-border" />
                </div>

                <div className="space-y-3 pl-2">
                  {dateActivities.map((activity) => {
                    const Icon =
                      activityIcons[activity.activity_type as keyof typeof activityIcons] ||
                      Clock;
                    return (
                      <div key={activity.id} className="flex gap-3">
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                          <Icon className="w-4 h-4 text-primary" />
                        </div>
                        <div className="flex-1 space-y-1">
                          <div className="flex items-center gap-2">
                            {activity.profiles?.name && (
                              <Avatar className="w-6 h-6">
                                <AvatarFallback className="text-xs">
                                  {activity.profiles.name.charAt(0)}
                                </AvatarFallback>
                              </Avatar>
                            )}
                            <span className="text-sm font-medium">
                              {activity.profiles?.name || "System"}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {formatDistanceToNow(new Date(activity.created_at), {
                                addSuffix: true,
                              })}
                            </span>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {activity.description}
                          </p>
                          {activity.activity_data &&
                            Object.keys(activity.activity_data).length > 0 && (
                              <div className="text-xs text-muted-foreground bg-muted p-2 rounded">
                                {JSON.stringify(activity.activity_data, null, 2)}
                              </div>
                            )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

            {!activities || activities.length === 0 && (
              <div className="text-center text-muted-foreground py-8">
                No activity yet
              </div>
            )}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
};
